import { useCallback, useEffect, useRef, useState } from 'react';
import { resolveUserConnectionIdentity } from '../utils/userConnectionDisplay';

export const DEFAULT_CONNECTION_MODAL_PAGE_SIZE = 48;
const EMPTY_ITEMS = Object.freeze([]);

const normalizePageSize = (value) => (
  Number.isFinite(value) && value > 0
    ? Math.min(100, Math.floor(value))
    : DEFAULT_CONNECTION_MODAL_PAGE_SIZE
);

const normalizeItems = (items) => (
  Array.isArray(items) ? items.filter(Boolean) : []
);

const mergeConnectionItems = (currentItems, nextItems) => {
  const seen = new Set();
  const merged = [];

  [...normalizeItems(currentItems), ...normalizeItems(nextItems)].forEach((item, index) => {
    const identity = resolveUserConnectionIdentity(item, `connection-${index}`);
    if (seen.has(identity)) {
      return;
    }
    seen.add(identity);
    merged.push(item);
  });

  return merged;
};

const resolveNextOffset = (page, fallbackOffset, receivedCount, pageSize) => {
  if (Number.isFinite(page?.nextOffset)) {
    return Math.max(0, Math.floor(page.nextOffset));
  }
  if (page?.hasMore === true) {
    return fallbackOffset + Math.max(receivedCount, pageSize);
  }
  return null;
};

export default function useOffsetConnectionList({
  enabled = true,
  loadPage,
  initialItems = EMPTY_ITEMS,
  pageSize,
  logLabel = 'connections',
} = {}) {
  const requestRef = useRef(0);
  const initialAbortRef = useRef(null);
  const normalizedPageSize = normalizePageSize(pageSize);
  const [state, setState] = useState({
    items: normalizeItems(initialItems),
    hasMore: false,
    nextOffset: null,
    totalCount: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
  });

  const abortInitialLoad = useCallback(() => {
    initialAbortRef.current?.abort();
    initialAbortRef.current = null;
  }, []);

  const loadInitialPage = useCallback(() => {
    abortInitialLoad();
    const seedItems = normalizeItems(initialItems);
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setState({
      items: seedItems,
      hasMore: false,
      nextOffset: null,
      totalCount: null,
      isLoading: Boolean(enabled && loadPage),
      isLoadingMore: false,
      error: null,
    });

    if (!enabled || typeof loadPage !== 'function') {
      return undefined;
    }

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    initialAbortRef.current = controller;
    Promise.resolve(loadPage({
      limit: normalizedPageSize,
      offset: 0,
      ...(controller ? { signal: controller.signal } : {}),
    }))
      .then((page) => {
        if (requestRef.current !== requestId) {
          return;
        }
        const pageItems = normalizeItems(page?.items);
        setState((current) => ({
          ...current,
          items: mergeConnectionItems(seedItems, pageItems),
          hasMore: page?.hasMore === true,
          nextOffset: resolveNextOffset(page, 0, pageItems.length, normalizedPageSize),
          totalCount: Number.isFinite(page?.totalCount) ? page.totalCount : null,
          isLoading: false,
          error: null,
        }));
      })
      .catch((error) => {
        if (controller?.signal?.aborted || requestRef.current !== requestId) {
          return;
        }
        console.warn(`Failed to load ${logLabel}.`, error);
        setState((current) => ({
          ...current,
          isLoading: false,
          error,
        }));
      })
      .finally(() => {
        if (requestRef.current === requestId && initialAbortRef.current === controller) {
          initialAbortRef.current = null;
        }
      });

    return undefined;
  }, [abortInitialLoad, enabled, initialItems, loadPage, logLabel, normalizedPageSize]);

  useEffect(() => {
    loadInitialPage();
    return () => {
      abortInitialLoad();
      requestRef.current += 1;
    };
  }, [abortInitialLoad, loadInitialPage]);

  const loadMore = useCallback(() => {
    if (!enabled || typeof loadPage !== 'function') {
      return;
    }

    if (state.isLoading || state.isLoadingMore || !state.hasMore) {
      return;
    }

    const nextOffset = Number.isFinite(state.nextOffset) ? state.nextOffset : state.items.length;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setState((current) => ({
      ...current,
      isLoadingMore: true,
      error: null,
    }));

    Promise.resolve(loadPage({
      limit: normalizedPageSize,
      offset: nextOffset,
    }))
      .then((page) => {
        if (requestRef.current !== requestId) {
          return;
        }
        const pageItems = normalizeItems(page?.items);
        setState((current) => ({
          ...current,
          items: mergeConnectionItems(current.items, pageItems),
          hasMore: page?.hasMore === true,
          nextOffset: resolveNextOffset(page, nextOffset, pageItems.length, normalizedPageSize),
          totalCount: Number.isFinite(page?.totalCount) ? page.totalCount : current.totalCount,
          isLoadingMore: false,
          error: null,
        }));
      })
      .catch((error) => {
        if (requestRef.current !== requestId) {
          return;
        }
        console.warn(`Failed to load more ${logLabel}.`, error);
        setState((current) => ({
          ...current,
          isLoadingMore: false,
          error,
        }));
      });
  }, [
    enabled,
    loadPage,
    logLabel,
    normalizedPageSize,
    state.hasMore,
    state.isLoading,
    state.isLoadingMore,
    state.items.length,
    state.nextOffset,
  ]);

  return {
    ...state,
    reload: loadInitialPage,
    loadMore,
  };
}
